import { Component, Input, AfterViewInit, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { AppService } from 'app/services/app/app.service';
import { SharedModule } from 'app/shared.module';
import { Chart } from 'chart.js/auto';

@Component({
  selector: 'app-chart-card',
  templateUrl: './chart-card.component.html',
  styleUrls: ['./chart-card.component.scss'],
  standalone: true,
  imports: [SharedModule]
})
export class ChartCardComponent implements AfterViewInit, OnDestroy {

  @ViewChild('chartCanvas')
  canvas!: ElementRef<HTMLCanvasElement>;

  @Input() title!: string;
  @Input() subtitle?: string;
  @Input() footer?: string;

  @Input() color = '#ffffff';
  @Input() type: 'line' | 'bar' | 'doughnut' = 'line';

  @Input() labels: string[] = [];
  @Input() datasets: any[] = [];

  chart!: Chart;

  specialLegend = ['doughnut'];

  legends:{ [key: string]: number } = {};

  backgroundColor:string[] = [];


constructor(public appService: AppService) { }



  get isSpecialLegend(): boolean {
    return this.specialLegend.includes(this.type);
  }

  get canvasHeight(): number {
    if (this.isSpecialLegend) {
      return this.isMobile ? (this.isCompactLegend ? 200 : 180) : 210;
    }

    return this.isMobile ? 200 : 250;
  }

  get isMobile(): boolean {
    return typeof window !== 'undefined' && window.innerWidth <= 768;
  }

  get isCompactLegend(): boolean {
    return this.isSpecialLegend && this.labels.length <= 2;
  }

  ngAfterViewInit() {
    this.createReservationChart(this.canvas.nativeElement);
    this.legends = this.convertChartToKeyValue();
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }
  }


  createReservationChart(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    const primaryColor = this.getCssVar('--color-primary', '#000000');
    const primaryRgb = this.getCssVar('--color-primary-rgb', '255, 131, 0');
    const accentColor = this.getCssVar('--color-accent', '#007cff');
    const primaryLight = this.getCssVar('--color-primary-300', '#ffb566');
    const primaryMid = this.getCssVar('--color-primary-400', '#fb923c');
    const primaryDeep = this.getCssVar('--color-primary-600', '#ea580c');

    this.backgroundColor = this.type === 'doughnut'
      ? [primaryColor, primaryMid, primaryLight, primaryDeep]
      : [accentColor, primaryLight, primaryColor];

    const gradient = ctx.createLinearGradient(0, 0, 0, this.canvasHeight);
    gradient.addColorStop(0, `rgba(${primaryRgb},0.35)`);
    gradient.addColorStop(1, `rgba(${primaryRgb},0.02)`);

    if (this.chart) {
      this.chart.destroy();
    }

    this.chart = new Chart(ctx, {
      type: this.type,

      data: {
        labels: this.labels.map(l => this.appService.getTraduccion(l)),
        datasets: this.datasets.map((d, i) => ({
          label: this.appService.getTraduccion(d.label),
          data: d.data,
          borderColor: this.type === 'doughnut'
            ? this.labels.map(() => '#fff7ed')
            : primaryColor,
          backgroundColor: this.type === 'line'
            ? gradient
            : this.labels.map((_, labelIndex) => this.backgroundColor[labelIndex % this.backgroundColor.length]),
          hoverBackgroundColor: this.type === 'line'
            ? gradient
            : this.labels.map((_, labelIndex) => this.backgroundColor[labelIndex % this.backgroundColor.length]),
          borderWidth: this.type === 'doughnut' ? 4 : 3,

          tension: 0.4,
          fill: this.type === 'line',

          pointRadius: this.type === 'line' ? (this.isMobile ? 3 : 5) : 0,
          pointHoverRadius: this.type === 'line' ? (this.isMobile ? 4 : 6) : 0,
          pointBackgroundColor: primaryColor,
          pointBorderWidth: 0,

          cutout: this.type === 'doughnut' ? '58%' : '65%',
          radius: this.type === 'doughnut' ? (this.isCompactLegend ? '92%' : (this.isMobile ? '78%' : '84%')) : undefined,
        }))
      },

      options: {

        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: this.type === 'doughnut'
            ? { top: 4, right: 4, bottom: 4, left: 4 }
            : { top: 6, right: 10, bottom: 0, left: 6 }
        },

        plugins: {

          legend: {
            display: false
          },

          tooltip: {
            backgroundColor: '#fff',
            titleColor: '#7a7a7a',
            bodyColor: '#222',
            borderColor: '#eee',
            borderWidth: 1,
            padding: 12,
            displayColors: false,

            callbacks: {
              title: (ctx) => ctx[0].label,
              label: (ctx) => `${ctx.raw}`
            }
          }
        },

        scales: this.type === 'doughnut' ? {} : {

          x: {
            grid: {
              display: false
            },
            ticks: {
              color: '#8c8c8c',
              autoSkip: true,
              maxRotation: 0,
              minRotation: 0,
              maxTicksLimit: this.isMobile ? 4 : 6,
              callback: (_value, index) => this.formatAxisLabel(this.labels[index] || '')
            }
          },

          y: {
            beginAtZero: true,

            grid: {
              //  borderDash: [4,4],
              color: '#e6e6e6'
            },

            ticks: {
              display: false
            }
          }
        }
      }
    });
  }

  convertChartToKeyValue(): { [key: string]: number } {
    if(this.labels == null) return {};
    if(this.datasets == null) return {};

    const result: { [key: string]: number } = {};

    // Recorremos las labels y buscamos su valor correspondiente en el primer dataset
    this.labels.forEach((label: string, index: number) => {
      // Tomamos el dato del primer dataset en la misma posición (index)
      const value = this.datasets[0].data[index];
      result[label] = value;
    });

    return result;
  }

  private getCssVar(name: string, fallback: string): string {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
  }

  private formatAxisLabel(label: string): string {
    if (!label) {
      return '';
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(label)) {
      const [, month, day] = label.split('-');
      return this.isMobile ? `${day}/${month}` : `${day}/${month}`;
    }

    return this.appService.getTraduccion(label);
  }
}
